import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { OrdersApiAdapter, OrdersFilters } from '../adapters/OrdersApiAdapter';

interface UseOrdersCountByStatusProps {
  search?: string;
  searchFilter?: { filters: any[]; logic: string };
  has_order_errors?: 1;
  general_error_ids?: string[];
  enabled?: boolean;
}

/**
 * Hook para obtener el recuento de pedidos por status
 * Respeta los filtros aplicados (búsqueda y filtros avanzados)
 */
export const useOrdersCountByStatus = ({
  search,
  searchFilter,
  has_order_errors,
  general_error_ids,
  enabled = true,
}: UseOrdersCountByStatusProps = {}) => {
  const adapter = useMemo(() => {
    const httpClient = createHttpClient(apiClientWH);
    return new OrdersApiAdapter(httpClient);
  }, []);

  const filters: OrdersFilters = useMemo(() => {
    const f: OrdersFilters = {};
    if (search) f.search = search;
    if (searchFilter && searchFilter.filters.length > 0) f.searchFilter = searchFilter;
    if (has_order_errors) f.has_order_errors = has_order_errors;
    if (general_error_ids?.length) f.general_error_ids = general_error_ids;
    return f;
  }, [search, searchFilter, has_order_errors, general_error_ids]);

  const queryKey = useMemo(
    () => [
      'orders-count-by-status',
      search,
      JSON.stringify(searchFilter),
      has_order_errors,
      JSON.stringify(general_error_ids),
    ],
    [search, searchFilter, has_order_errors, general_error_ids],
  );

  const {
    data: counts = {},
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey,
    queryFn: () => adapter.getOrdersCountByStatus(filters),
    enabled,
    staleTime: 30 * 1000, // 30 segundos de cache
    refetchOnWindowFocus: false,
  });

  return {
    counts,
    isLoading,
    error,
    refetch,
  };
};
